import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Button,
} from "@mui/material";

import {
    CheckCircle,
    Circle,
    Clear,
    CloudDone,
    DirectionsCar,
    Factory,
    Inventory2,
    LocalShipping,
    Memory,
    Person,
    Router,
    Search,
    Settings,
    SimCard,
    Smartphone,
    Wifi,
} from "@mui/icons-material";

import InventoryServices from "../../services/InventoryServices";

/* =========================================================
   CONSTANTS
========================================================= */

const PAGE_SIZE = 50;

const STEPS = [
    {
        key: "esim_uploaded",
        label: "eSIM Uploaded",
        shortLabel: "eSIM Upload",
        description: "Page 1 inventory upload",
        icon: <SimCard />,
    },
    {
        key: "stock_tagged",
        label: "Stock Tagged",
        shortLabel: "Factory Match",
        description: "IMEI / ICCID factory match",
        icon: <Inventory2 />,
    },
    {
        key: "bootstrap_activated",
        label: "Bootstrap Activated",
        shortLabel: "Bootstrap",
        description: "Bootstrap result uploaded",
        icon: <CloudDone />,
    },
    {
        key: "fota_config_done",
        label: "FOTA / Config Done",
        shortLabel: "FOTA + Config",
        description: "FOTA and configuration completed",
        icon: <Settings />,
    },
    {
        key: "dispatched_to_manufacturer",
        label: "Dispatched to Manufacturer",
        shortLabel: "Manufacturer",
        description: "Factory → manufacturer dispatch",
        icon: <Factory />,
    },
    {
        key: "dispatched_to_dealer",
        label: "Dispatched to Dealer",
        shortLabel: "Dealer",
        description: "Manufacturer → dealer dispatch",
        icon: <LocalShipping />,
    },
    {
        key: "tagging_done",
        label: "Tagging Done",
        shortLabel: "Vehicle Tag",
        description: "Active vehicle-owner DeviceTag",
        icon: <DirectionsCar />,
    },
    {
        key: "installation_done",
        label: "Installation Done",
        shortLabel: "Installation",
        description: "Full on-site installation submitted",
        icon: <Router />,
    },
    {
        key: "certificate_uploaded",
        label: "Certificate Uploaded",
        shortLabel: "Certificate",
        description: "Certificate file uploaded",
        icon: <CheckCircle />,
    },
];

/* =========================================================
   HELPERS
========================================================= */

const getResponseData = (response) => {
    return response?.data ?? response ?? {};
};

const getRowsFromResponse = (response) => {
    const data = getResponseData(response);

    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    if (Array.isArray(data?.rows)) {
        return data.rows;
    }

    if (Array.isArray(data?.items)) {
        return data.items;
    }

    return [];
};

const getSummaryFromResponse = (response) => {
    const data = getResponseData(response);

    if (Array.isArray(data?.summary)) {
        return data.summary;
    }

    return [];
};

const getTotalFromResponse = (response, rows = []) => {
    const data = getResponseData(response);

    return (
        data?.count ??
        data?.total ??
        data?.total_count ??
        data?.pagination?.count ??
        rows.length
    );
};

const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") {
        return "0";
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
        return String(value);
    }

    return number.toLocaleString("en-IN");
};

const formatDateTime = (value) => {
    if (!value) {
        return "—";
    }

    try {
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    } catch {
        return value;
    }
};

const formatLabel = (value) => {
    if (!value) {
        return "Not Uploaded";
    }

    return String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getLocationName = (location) => {
    if (!location) {
        return "—";
    }

    if (typeof location === "string") {
        return location;
    }

    return location?.name || location?.stage || "—";
};

const getLocationStage = (location) => {
    if (!location || typeof location === "string") {
        return "";
    }

    return location?.stage || "";
};

const getOnlineStatus = (value) => {
    if (value === true) {
        return true;
    }

    if (
        typeof value === "string" &&
        ["true", "online", "1", "yes"].includes(value.toLowerCase())
    ) {
        return true;
    }

    return false;
};

/* =========================================================
   SUMMARY CARD
========================================================= */

const SummaryCard = ({ step, summary }) => {
    const done = Number(summary?.done || 0);
    const stuck = Number(summary?.stuck_here || 0);

    return (
        <Card
            sx={{
                height: "100%",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                    boxShadow: 3,
                    transform: "translateY(-2px)",
                },
            }}
        >
            <CardContent sx={{ p: 2 }}>
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="flex-start"
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            flexShrink: 0,
                        }}
                    >
                        {step.icon}
                    </Box>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                                lineHeight: 1.3,
                            }}
                        >
                            {step.label}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {step.description}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                        >
                            Done
                        </Typography>

                        <Typography variant="h6" fontWeight={700}>
                            {formatNumber(done)}
                        </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                        >
                            Stuck Here
                        </Typography>

                        <Typography
                            variant="h6"
                            fontWeight={700}
                            color={stuck > 0 ? "warning.main" : "success.main"}
                        >
                            {formatNumber(stuck)}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

/* =========================================================
   STATUS CHIP
========================================================= */

const StatusChip = ({ status }) => {
    if (!status) {
        return (
            <Chip
                size="small"
                label="Not Uploaded"
                variant="outlined"
            />
        );
    }

    return (
        <Chip
            size="small"
            label={formatLabel(status)}
            color="primary"
            variant="outlined"
        />
    );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const StockMasterStatusOverview = () => {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState([]);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [stuckAt, setStuckAt] = useState("");
    const [sortBy, setSortBy] = useState("-dispatched_at");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error",
    });

    /* =====================================================
       SUMMARY MAP
    ===================================================== */

    const summaryMap = useMemo(() => {
        const map = {};

        summary.forEach((item) => {
            if (item?.step) {
                map[item.step] = item;
            }
        });

        return map;
    }, [summary]);

    /* =====================================================
       API CALL
    ===================================================== */

    const fetchOverview = useCallback(
        async (requestedPage = page) => {
            setLoading(true);

            try {
                const payload = {
                    filters: {},
                    sort_by: sortBy,
                    page: requestedPage,
                    page_size: PAGE_SIZE,
                };

                if (stuckAt) {
                    payload.filters.stuck_at = stuckAt;
                }

                /*
                 * Search field is intentionally sent in filters
                 * so it can be adjusted easily if the backend
                 * expects a different filter key.
                 */
                if (search.trim()) {
                    payload.filters.search = search.trim();
                }

                const response =
                    await InventoryServices.stockMasterStatusOverview(
                        payload
                    );

                const responseData = getResponseData(response);

                const responseRows = getRowsFromResponse(response);
                const responseSummary = getSummaryFromResponse(response);

                setRows(responseRows);
                setSummary(responseSummary);
                setTotal(
                    getTotalFromResponse(response, responseRows)
                );

                /*
                 * If backend returns a pagination object with
                 * current page, respect it.
                 */
                if (responseData?.pagination?.page) {
                    setPage(Number(responseData.pagination.page));
                }
            } catch (error) {
                console.error(
                    "Stock master status overview error:",
                    error
                );

                const message =
                    error?.response?.data?.message ||
                    error?.response?.data?.detail ||
                    error?.response?.data?.error ||
                    "Failed to load stock master status overview.";

                setSnackbar({
                    open: true,
                    message,
                    severity: "error",
                });

                setRows([]);
                setSummary([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        },
        [page, search, sortBy, stuckAt]
    );

    /* =====================================================
       INITIAL / FILTER FETCH
    ===================================================== */

    useEffect(() => {
        fetchOverview(page);
    }, [fetchOverview, page]);

    /* =====================================================
       FILTER HANDLERS
    ===================================================== */

    const handleStuckAtChange = (event) => {
        setStuckAt(event.target.value);
        setPage(1);
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
        setPage(1);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter") {
            setPage(1);
            fetchOverview(1);
        }
    };

const handleClearSearch = () => {
    setSearch("");
    setPage(1);
};

const handleClearFilters = () => {
    setSearch("");
    setStuckAt("");
    setSortBy("-dispatched_at");
    setPage(1);
};

    const handlePageChange = (_, value) => {
        setPage(value);
    };

    const handleSnackbarClose = () => {
        setSnackbar((previous) => ({
            ...previous,
            open: false,
        }));
    };

    const totalPages = Math.max(
        1,
        Math.ceil(total / PAGE_SIZE)
    );

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <Box sx={{ p: 2 }}>
            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                    >
                        Stock Status Overview
                    </Typography>

                    {/* <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Track inventory units through all 9
                        activation and deployment stages.
                    </Typography> */}
                </Box>
            </Stack>

            {/* =================================================
                FUNNEL SUMMARY
            ================================================= */}

            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 1.5 }}
                >
                    Progress Summary
                </Typography>

                <Grid container spacing={2}>
                    {STEPS.map((step) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            xl={2.4}
                            key={step.key}
                        >
                            <SummaryCard
                                step={step}
                                summary={summaryMap[step.key]}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* =================================================
                FILTERS
            ================================================= */}

            <Card
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                }}
            >
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        {/* Search */}

                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                placeholder="IMEI, ICCID, vehicle reg no..."
                                value={search}
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                                onKeyDown={handleSearchKeyDown}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText="Press Enter to search"
                            />
                        </Grid>

                        {/* Stuck At */}

                        <Grid item xs={12} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel>
                                    Stuck At
                                </InputLabel>

                                <Select
                                    value={stuckAt}
                                    label="Stuck At"
                                    onChange={
                                        handleStuckAtChange
                                    }
                                >
                                    <MenuItem value="">
                                        All Stages
                                    </MenuItem>

                                    {STEPS.map((step) => (
                                        <MenuItem
                                            key={step.key}
                                            value={step.key}
                                        >
                                            {step.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Sort */}

                        <Grid item xs={12} md={3}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel>
                                    Sort By
                                </InputLabel>

                                <Select
                                    value={sortBy}
                                    label="Sort By"
                                    onChange={handleSortChange}
                                >
                                    <MenuItem value="-dispatched_at">
                                        Latest Dispatch
                                    </MenuItem>

                                    <MenuItem value="dispatched_at">
                                        Oldest Dispatch
                                    </MenuItem>

                                    <MenuItem value="-created_at">
                                        Latest Created
                                    </MenuItem>

                                    <MenuItem value="created_at">
                                        Oldest Created
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={12}>
    <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
    >
        <Button
            variant="outlined"
            color="inherit"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            disabled={
                !search &&
                !stuckAt &&
                sortBy === "-dispatched_at"
            }
        >
            Clear Filters
        </Button>
    </Stack>
</Grid>
                    </Grid>

                    {search && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                                size="small"
                                label={`Search: ${search}`}
                                onDelete={handleClearSearch}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* =================================================
                TABLE
            ================================================= */}

            <Card
                sx={{
                    borderRadius: 2,
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: "divider",
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            p: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                            >
                                Unit Details
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {formatNumber(total)} total units
                            </Typography>
                        </Box>

                        {loading && (
                            <CircularProgress size={24} />
                        )}
                    </Box>

                    <Divider />

                   <TableContainer
    sx={{
        maxHeight: 650,
        overflowX: "auto",
    }}
>
    <Table
        stickyHeader
        size="small"
        sx={{
            minWidth: 5000,
            whiteSpace: "nowrap",
        }}
    >
        <TableHead>
            <TableRow>
                {/* <TableCell sx={{ fontWeight: 700 }}>#</TableCell> */}
                <TableCell sx={{ fontWeight: 700 }}>Stock Master ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>UID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>IMEI</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ICCID Primary</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ICCID Secondary</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Device ESN</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>MSISDN Primary</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>MSISDN Fallback</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Firmware Version</TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    eSIM Bootstrap Activation Date
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    eSIM Commercial Activation Date
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    eSIM Validity
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    eSIM Card Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Backend Configured
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    IP/Port Configured
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Vehicle Reg. No.
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Chassis No.
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Current Activation Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Current Activation Stage
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Commercial Activation Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Bootstrap Activation Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Device Online
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Last Online At
                </TableCell>

                {/* <TableCell sx={{ fontWeight: 700 }}>
                    Manufacturer ID
                </TableCell> */}

                <TableCell sx={{ fontWeight: 700 }}>
                    Manufacturer Name
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Device Model ID
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Device Model Name
                </TableCell>

                {/* <TableCell sx={{ fontWeight: 700 }}>
                    Dealer ID
                </TableCell> */}

                <TableCell sx={{ fontWeight: 700 }}>
                    Dealer Name
                </TableCell>

                {/* <TableCell sx={{ fontWeight: 700 }}>
                    Owner ID
                </TableCell> */}

                <TableCell sx={{ fontWeight: 700 }}>
                    Owner Name
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Location Stage
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Location Name
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Dispatch Vendor
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Dispatched At
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Plan Name
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Commercial Plan Name
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Provider SIM Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Deleted
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Created At
                </TableCell>

                <TableCell sx={{ fontWeight: 700 }}>
                    Updated At
                </TableCell>
            </TableRow>
        </TableHead>

        <TableBody>
            {loading && rows.length === 0 ? (
                <TableRow>
                    <TableCell
                        colSpan={42}
                        align="center"
                        sx={{ py: 8 }}
                    >
                        <CircularProgress />

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            Loading stock status...
                        </Typography>
                    </TableCell>
                </TableRow>
            ) : rows.length === 0 ? (
                <TableRow>
                    <TableCell
                        colSpan={42}
                        align="center"
                        sx={{ py: 8 }}
                    >
                        <Typography
                            variant="body1"
                            fontWeight={600}
                        >
                            No data found
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            No stock units match the selected filters.
                        </Typography>
                    </TableCell>
                </TableRow>
            ) : (
                rows.map((row, index) => {
                    const online = getOnlineStatus(
                        row.device_online
                    );

                    const locationStage =
                        typeof row.location === "object"
                            ? row.location?.stage
                            : "";

                    const locationName =
                        typeof row.location === "object"
                            ? row.location?.name
                            : row.location || "—";

                    return (
                        <TableRow
                            hover
                            key={
                                row.stock_master_id ||
                                row.id ||
                                row.uid ||
                                index
                            }
                        >
                            {/* # */}
                            {/* <TableCell>
                                {(page - 1) * PAGE_SIZE +
                                    index +
                                    1}
                            </TableCell> */}

                            {/* Stock Master ID */}
                            <TableCell>
                                {row.stock_master_id ?? "—"}
                            </TableCell>

                            {/* UID */}
                            <TableCell>
                                {row.uid || "—"}
                            </TableCell>

                            {/* IMEI */}
                            <TableCell>
                                {row.imei ||
                                    row.IMEI ||
                                    "—"}
                            </TableCell>

                            {/* ICCID Primary */}
                            <TableCell>
                                {row.iccid_primary || "—"}
                            </TableCell>

                            {/* ICCID Secondary */}
                            <TableCell>
                                {row.iccid_secondary || "—"}
                            </TableCell>

                            {/* Device ESN */}
                            <TableCell>
                                {row.device_esn || "—"}
                            </TableCell>

                            {/* MSISDN Primary */}
                            <TableCell>
                                {row.msisdn_primary || "—"}
                            </TableCell>

                            {/* MSISDN Fallback */}
                            <TableCell>
                                {row.msisdn_fallback || "—"}
                            </TableCell>

                            {/* Firmware */}
                            <TableCell>
                                {row.firmware_version || "—"}
                            </TableCell>

                            {/* Bootstrap Activation Date */}
                            <TableCell>
                                {formatDateTime(
                                    row.esim_bootstrap_activation_date
                                )}
                            </TableCell>

                            {/* Commercial Activation Date */}
                            <TableCell>
                                {formatDateTime(
                                    row.esim_commercial_activation_date
                                )}
                            </TableCell>

                            {/* eSIM Validity */}
                            <TableCell>
                                {formatDateTime(
                                    row.esim_validity
                                )}
                            </TableCell>

                            {/* eSIM Card Status */}
                            <TableCell>
                                {row.esim_card_status || "—"}
                            </TableCell>

                            {/* Backend Configured */}
                            <TableCell>
                                {row.backend_configured ? (
                                    <Chip
                                        size="small"
                                        label="Configured"
                                        color="success"
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="Not Configured"
                                        variant="outlined"
                                    />
                                )}
                            </TableCell>

                            {/* IP Port Configured */}
                            <TableCell>
                                {row.ip_port_configured ? (
                                    <Chip
                                        size="small"
                                        label="Configured"
                                        color="success"
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="Not Configured"
                                        variant="outlined"
                                    />
                                )}
                            </TableCell>

                            {/* Vehicle Reg No */}
                            <TableCell>
                                {row.vehicle_reg_no || "—"}
                            </TableCell>

                            {/* Chassis No */}
                            <TableCell>
                                {row.chassis_no || "—"}
                            </TableCell>

                            {/* Current Activation Status */}
                            <TableCell>
                                {row.current_activation_status ||
                                    "—"}
                            </TableCell>

                            {/* Current Activation Stage */}
                            <TableCell>
    <StatusChip
        status={
            row.current_activation_status ||
            row.current_activation_stage_key
        }
    />
</TableCell>

                            {/* Commercial Activation Status */}
                            <TableCell>
                                {row.commercial_activation_status_raw ||
                                    "—"}
                            </TableCell>

                            {/* Bootstrap Activation Status */}
                            <TableCell>
                                {row.bootstrap_activation_status_raw ||
                                    "—"}
                            </TableCell>

                            {/* Device Online */}
                            <TableCell>
                                <Chip
                                    size="small"
                                    icon={<Wifi />}
                                    label={
                                        online
                                            ? "Online"
                                            : "Offline"
                                    }
                                    color={
                                        online
                                            ? "success"
                                            : "default"
                                    }
                                    variant={
                                        online
                                            ? "filled"
                                            : "outlined"
                                    }
                                />
                            </TableCell>

                            {/* Last Online */}
                            <TableCell>
                                {formatDateTime(
                                    row.last_online_at
                                )}
                            </TableCell>

                            {/* Manufacturer ID */}
                            {/* <TableCell>
                                {row.manufacturer_id ?? "—"}
                            </TableCell> */}

                            {/* Manufacturer Name */}
                            <TableCell>
                                {row.manufacturer_name || "—"}
                            </TableCell>

                            {/* Device Model ID */}
                            <TableCell>
                                {row.device_model_id ?? "—"}
                            </TableCell>

                            {/* Device Model Name */}
                            <TableCell>
                                {row.device_model_name || "—"}
                            </TableCell>

                            {/* Dealer ID */}
                            {/* <TableCell>
                                {row.dealer_id ?? "—"}
                            </TableCell> */}

                            {/* Dealer Name */}
                            <TableCell>
                                {row.dealer_name || "—"}
                            </TableCell>

                            {/* Owner ID */}
                            {/* <TableCell>
                                {row.owner_id ?? "—"}
                            </TableCell> */}

                            {/* Owner Name */}
                            <TableCell>
                                {row.owner_name || "—"}
                            </TableCell>

                            {/* Location Stage */}
                            <TableCell>
                                {locationStage
                                    ? formatLabel(locationStage)
                                    : "—"}
                            </TableCell>

                            {/* Location Name */}
                            <TableCell>
                                {locationName}
                            </TableCell>

                            {/* Dispatch Vendor */}
                            <TableCell>
                                {row.dispatch_vendor || "—"}
                            </TableCell>

                            {/* Dispatched At */}
                            <TableCell>
                                {formatDateTime(
                                    row.dispatched_at
                                )}
                            </TableCell>

                            {/* Plan Name */}
                            <TableCell>
                                {row.plan_name || "—"}
                            </TableCell>

                            {/* Commercial Plan */}
                            <TableCell>
                                {row.commercial_plan_name ||
                                    "—"}
                            </TableCell>

                            {/* Provider SIM Status */}
                            <TableCell>
                                {row.provider_sim_status || "—"}
                            </TableCell>

                            {/* Deleted */}
                            <TableCell>
                                {row.is_deleted ? (
                                    <Chip
                                        size="small"
                                        label="Yes"
                                        color="error"
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="No"
                                        color="success"
                                        variant="outlined"
                                    />
                                )}
                            </TableCell>

                            {/* Created At */}
                            <TableCell>
                                {formatDateTime(
                                    row.created_at
                                )}
                            </TableCell>

                            {/* Updated At */}
                            <TableCell>
                                {formatDateTime(
                                    row.updated_at
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })
            )}
        </TableBody>
    </Table>
</TableContainer>

                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    {rows.length > 0 && (
                        <>
                            <Divider />

                            <Box
                                sx={{
                                    p: 2,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Page {page} of {totalPages}
                                </Typography>

                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={
                                        handlePageChange
                                    }
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* =================================================
                SNACKBAR
            ================================================= */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StockMasterStatusOverview;
